import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { artifacts as artifactsApi } from '../api'

/**
 * Debounced server-side artifact picker hook.
 *
 * - Empty filter → returns the most-recent N artifacts (server-sorted).
 * - On typing → 250ms debounce, then ?search=<q>&limit=N hits the
 *   artifact list endpoint (which already has a SearchFilter on
 *   subject / from_email / cached_content).
 * - Result list is capped at `limit` (default 50) to keep render fast.
 *
 * Used by the prompt dry-run picker, pipeline-test picker, and any
 * future "pick an artifact to test against" UI.
 */
export function useArtifactPicker({
  limit = 50,
  ignoredFilter = 'false',
}: { limit?: number; ignoredFilter?: 'all' | 'true' | 'false' } = {}) {
  const [filter, setFilter] = useState('')
  const [debouncedFilter, setDebouncedFilter] = useState('')

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedFilter(filter.trim()), 250)
    return () => window.clearTimeout(id)
  }, [filter])

  const params: Record<string, string | number | boolean> = { limit, ignored: ignoredFilter }
  if (debouncedFilter) params.search = debouncedFilter

  const query = useQuery({
    queryKey: ['artifacts', 'picker', { search: debouncedFilter, limit, ignoredFilter }],
    queryFn: () => artifactsApi.list(params),
    placeholderData: (prev) => prev,  // keep prior results visible while typing
  })

  return {
    filter,
    setFilter,
    artifacts: query.data ?? [],
    isFetching: query.isFetching,
    isPending: query.isPending,
  }
}
